<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
        <Topbar />
        
        <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
          <Toast />

          <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
            <div>
              <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Сотрудники</h2>
              <p class="mt-1 text-sm text-slate-500 md:text-base">Список сотрудников, тарифов и операций по оплате труда</p>
            </div>

            <div class="flex flex-wrap items-center justify-end gap-2">
              <Button icon="pi pi-plus" label="Добавить сотрудника" severity="success" class="!px-3 !py-2 !text-sm !shadow-sm" @click="openNew" />
              <Button icon="pi pi-upload" label="Экспорт" severity="info" class="!px-3 !py-2 !text-sm !shadow-sm" @click="exportCSV($event)" />
            </div>
          </div>
      
          <div class="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6"> 
              <div class="mb-4 flex shrink-0 items-center justify-between gap-3">
                  <div class="max-w-2xl flex-1">
                      <IconField class="w-full">
                          <InputIcon><i class="pi pi-search" /></InputIcon>
                          <InputText v-model.trim="searchQuery" class="w-full" placeholder="Поиск по ФИО или должности..." />
                      </IconField>
                  </div>

                  <div class="flex items-center gap-2">
                      <Button
                          label="Сетка"
                          icon="pi pi-th-large"
                          size="small"
                          :severity="viewMode === 'grid' ? 'success' : 'secondary'"
                          :outlined="viewMode !== 'grid'"
                          @click="viewMode = 'grid'"
                      />
                      <Button
                          label="Таблица"
                          icon="pi pi-table"
                          size="small"
                          :severity="viewMode === 'table' ? 'success' : 'secondary'"
                          :outlined="viewMode !== 'table'"
                          @click="viewMode = 'table'"
                      />
                  </div>
              </div>

              <div class="mb-4 flex shrink-0 flex-wrap items-center justify-start gap-2">
                  <Button icon="pi pi-clock" severity="info" outlined label="Учет времени" @click="goToWorkingHours()"/>
                  <Button icon="pi pi-calendar-clock" severity="secondary" outlined label="График план" @click="goToGraphPlan()"/>
                  <Button icon="pi pi-calendar-clock" severity="warn" outlined label="График факт" @click="openGraphFact()"/>
                  <Button icon="pi pi-credit-card" severity="success" outlined label="Отчет ЗП" @click="openSalaryReport()"/>
              </div>

              <div v-if="!filteredEmployees.length" class="h-full overflow-y-auto pr-1">
                  <div class="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
                      Сотрудники не найдены
                  </div>
              </div>

              <div v-else-if="viewMode === 'grid'" class="h-full overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 pr-1">
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      <article
                          v-for="item in filteredEmployees"
                          :key="item.id"
                          class="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-emerald-200 hover:bg-emerald-50/20"
                      >
                          <div class="absolute right-4 top-4 flex items-center gap-1">
                              <Button icon="pi pi-wallet" severity="info" text rounded @click="showPayment(item)"></Button>
                              <Button icon="pi pi-trash" severity="danger" text rounded @click="confirmDeleteProduct(item)"></Button>
                          </div>

                          <div class="flex items-start justify-between gap-3">
                              <div class="flex min-w-0 items-start gap-3">
                                  <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-base font-semibold text-white">
                                      {{ initialFromName(item.name) }}
                                  </div>
                                  <div class="min-w-0">
                                      <p class="truncate text-lg font-semibold text-slate-900">{{ item.name || 'Без имени' }}</p>
                                      <span class="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                          {{ item.position || 'Сотрудник' }}
                                      </span>
                                      <p class="mt-4 text-sm text-slate-500">
                                          Оклад:
                                          <span class="font-semibold text-slate-900">{{ formatCurrency(item.tariff) }}</span>
                                      </p>
                                      <p class="mt-1 text-sm text-slate-500">С: {{ formatDate(item.date) }}</p>
                                      <p class="mt-1 text-sm text-slate-500">Штрихкод: {{ item.barcode || '—' }}</p>
                                  </div>
                              </div>
                          </div>
                      </article>
                  </div>
              </div>

              <DataTable
                  v-else
                  ref="employeeTable" :value="filteredEmployees" :paginator="true" :loading="loading" :rows="10" :rowsPerPageOptions="[5, 10]"
                  dataKey="id" size="small" resizableColumns columnResizeMode="fit" showGridlines scrollable scrollHeight="flex"
                  class="employee-table h-full"
              >
                  <template #header>
                      <div></div>
                  </template>
              
                  <Column field="name" header="Сотрудник" sortable style="min-width: 16rem" frozen>
                      <template #body="{ data }">
                          <div class="flex min-w-0 items-center gap-3">
                              <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-700">
                                  {{ initialFromName(data.name) }}
                              </div>
                              <div class="min-w-0">
                                  <p class="truncate font-semibold text-slate-900">{{ data.name || 'Без имени' }}</p>
                                  <p class="truncate text-xs text-slate-500">{{ data.position || 'Должность не указана' }}</p>
                              </div>
                          </div>
                      </template>
                  </Column>
                  <Column field="position" header="Должность" sortable style="min-width: 12rem">
                      <template #body="{ data }">
                          <span class="text-slate-600">{{ data.position || '—' }}</span>
                      </template>
                  </Column>
                  <Column field="barcode" header="Штрихкод" sortable style="min-width: 10rem">
                      <template #body="{ data }">
                          <span class="text-slate-600">{{ data.barcode || '—' }}</span>
                      </template>
                  </Column>
                  <Column field="date" header="С даты" sortable style="min-width: 9rem">
                      <template #body="{ data }">
                          <span class="text-slate-600">{{ formatDate(data.date) }}</span>
                      </template>
                  </Column>
                  <Column field="payment_method" header="Способ оплаты" sortable style="min-width: 12rem">
                      <template #body="{ data }">
                          <span class="text-slate-600">{{ data.payment_method || '—' }}</span>
                      </template>
                  </Column>
                  <Column field="tariff" header="Тариф" sortable style="min-width: 10rem;">
                      <template #body="{ data }">
                          <span class="font-semibold text-slate-900">{{ formatCurrency(data.tariff) }}</span>
                      </template>
                  </Column>
                  <Column :exportable="false" header="Оплата ЗП" class="!text-center" style="width: 8rem">
                      <template #body="{ data }">
                          <Button icon="pi pi-wallet" severity="info" text rounded @click="showPayment(data)"></Button>
                      </template>
                  </Column>
                  <Column :exportable="false" header="Сменить пароль" class="!text-center" style="width: 9rem">
                      <template #body="{ data }">
                          <Button icon="pi pi-key" severity="secondary" text rounded @click="showChangePassword(data)"></Button>
                      </template>
                  </Column>
                  <Column :exportable="false" header="Удалить" class="!text-center" frozen style="width: 7rem">
                      <template #body="{ data }">
                          <Button icon="pi pi-trash" severity="danger" text rounded @click="confirmDeleteProduct(data)" />
                      </template>
                  </Column>
              </DataTable>
          </div>
      
          <Dialog v-model:visible="employeeDialog" :style="{ width: '500px' }" header="Форма Сотрудника" :modal="true">
              <div class="flex flex-col gap-2">
                  <div class="py-1">
                      <FloatLabel variant="on">
                          <label for="name" class="block"> ФИО </label>
                          <InputText id="name" v-model.trim="employee.name" required= autofocus :invalid="submitted && !employee.name" fluid />
                      </FloatLabel>
                      <small v-if="submitted && !employee.name" class="text-red-500">Заполните!</small>
                  </div>
                  <div class="py-1">
                      <FloatLabel variant="on" >
                          <label for="position" class="block"> Должность </label>
                          <InputText id="position" v-model.trim="employee.position" required autofocus :invalid="submitted && !employee.position" fluid/>
                      </FloatLabel>
                      <small v-if="submitted && !employee.position" class="text-red-500">Заполните!</small>
                  </div>
                  <div class="grid grid-cols-12 gap-2 py-1">
                      <div class="col-span-6">
                          <FloatLabel variant="on">
                              <label for="payment_method_id" class="block z-10">Способ оплаты</label>
                              <Select id="payment_method_id" v-model="employee.payment_method_id" :options="paymentMethods" :invalid="submitted && !employee.payment_method_id" optionLabel="name" optionValue="id" fluid required></Select>
                          </FloatLabel>
                          <small v-if="submitted && !employee.payment_method_id" class="text-red-500 ">Заполните!</small>
                      </div>
                      <div class="col-span-6">
                          <FloatLabel variant="on" >
                              <label for="currency" class="block z-10">Валюта</label>
                              <Select id="currency" v-model="employee.currency_id" :options="currencies" :invalid="submitted && !employee.currency_id" optionLabel="name" optionValue="id" fluid required></Select>
                          </FloatLabel>
                          <small v-if="submitted && !employee.currency_id" class="text-red-500">Заполните!</small>
                      </div>
                  </div>
              
                  <div class="grid grid-cols-12 gap-2 py-1 mt-1">
                      <div class="col-span-6">
                          <FloatLabel variant="on">
                              <label for="date" class="z-10"> Период с </label>
                              <DatePicker id="date" v-model="employee.date" :invalid="submitted && !employee.date" size="small" inputId="on_label" showIcon iconDisplay="input" fluid required/>
                          </FloatLabel>
                      </div>
                      <div class="col-span-6">
                          <FloatLabel variant="on" class="h-full">
                              <label for="tariff" class="block z-10">Тариф</label>
                              <InputNumber id="tariff" v-model="employee.tariff" :invalid="submitted && !employee.tariff" mode="decimal" fluid class="h-full" required/>
                          </FloatLabel>
                      </div>
                  </div>
              
                  <div class="mt-1 py-1">
                      <FloatLabel variant="on" >
                          <label for="password" class="block"> Пароль </label>
                          <InputText id="password" v-model="employee.password" required autofocus :invalid="submitted && !employee.password" fluid/>
                      </FloatLabel>
                      <small v-if="submitted && !employee.password" class="text-red-500">Заполните!</small>
                  </div>
                  <div class="mt-1 py-1">
                      <FloatLabel variant="on" >
                          <label for="password_confirmation" class="block"> Повторите пароль </label>
                          <InputText id="password_confirmation" v-model="employee.password_confirmation" required autofocus :invalid="submitted && !employee.password_confirmation" fluid/>
                      </FloatLabel>
                      <small v-if="submitted && !employee.password_confirmation" class="text-red-500">Заполните!</small>
                  </div>
              </div>
          
              <template #footer>
                  <Button label="Добавить" icon="pi pi-check" @click="saveProduct" severity="success" />
              </template>
          </Dialog>
      
          <Dialog v-model:visible="paymentDialog"  header="Оплата труда" :modal="true" class="p-fluid w-full">    
              <div class="flex flex-row justify-between items-center gap-2 mt-2">
                  <div class="flex gap-2 items-center">
                      <FloatLabel variant="on">
                          <label for="on_label">ФИО</label>
                          <InputText id="on_label" v-model="selectedEmployee.name" disabled variant="outlined" class="h-11"/>
                      </FloatLabel>
                      <FloatLabel variant="on">
                          <label for="on_label" class="z-10">Должность</label>
                          <InputText id="on_label" v-model="selectedEmployee.position" disabled variant="outlined" class="h-11"/>
                      </FloatLabel>
                      <FloatLabel variant="on">
                          <label for="on_label" class="z-10">Тариф</label>
                          <InputText id="on_label" v-model="selectedEmployee.tariff" disabled variant="outlined" class="h-11"/>
                      </FloatLabel>
                      <FloatLabel variant="on">
                          <label for="on_label" class="z-10">Месяц оплаты</label>
                          <DatePicker v-model="paymentMonth" :invalid="!paymentMonth" view="month" size="small" dateFormat="yy-mm" inputId="on_label" showIcon iconDisplay="input" required class="w-50"/>
                      </FloatLabel>
                      <FloatLabel variant="on">
                          <label for="on_label" class="z-10">Период оплаты</label>
                          <Select id="currency" class="w-50 h-11 items-center" v-model="selectedEmployee.paymentPeriod" :options="paymentPeriods" :invalid="submitted && !selectedEmployee.paymentPeriod" optionLabel="name" optionValue="id" required></Select>
                      </FloatLabel>
                  </div>
                  <div class="flex gap-2 items-center">
                      <FloatLabel variant="on">
                          <label for="on_label" class="z-10"> Сумма оплаты </label>
                          <InputNumber id="on_label" v-model="selectedEmployee.payment" :max="totalSum" :min="0" variant="outlined" class="h-11" required/>
                      </FloatLabel>
                      <FloatLabel variant="on">
                          <label for="on_label" class="z-10"> Аванс </label>
                          <InputNumber id="on_label" v-model="selectedEmployee.prepayment" :max="totalSum" :min="0" variant="outlined" class="h-11" required/>
                      </FloatLabel>
                  </div>
              </div>
              <div class="flex justify-start my-2">
                    <FloatLabel variant="on" class="mr-2">
                        <label for="on_label" class="z-10">Счет</label>
                        <Select v-model="selectedEmployee.account" showClear :options="accounts" filter optionLabel="account" class="w-full md:w-56" required></Select>
                    </FloatLabel>
                    <FloatLabel variant="on" v-if="selectedEmployee.account" class="mr-2">
                        <label for="on_label" aria-disabled>Банк</label>
                        <InputText id="on_label" v-model="selectedEmployee.account.bank" disabled variant="outlined"/>
                    </FloatLabel>
                    <FloatLabel variant="on" v-if="selectedEmployee.account">
                        <label for="on_label" class="z-10">Сумма</label>
                        <InputNumber id="on_label" v-model="selectedEmployee.account.amount" disabled variant="outlined" :suffix="' ' + selectedEmployee.account.currency"/>
                    </FloatLabel>
              </div>
              <!-- <div class="flex justify-between">
                  <FileUpload ref="fileupload" chooseLabel="Добавить файл" mode="basic" name="demo[]" url="/api/upload" accept="image/*" :maxFileSize="1000000":chooseButtonProps="{ severity: 'info' }" required/>
                  <Button label="Оплатить" @click="payEmployee()" severity="success" icon="pi pi-check" iconPos="right"/>
              </div> -->
              <div class="mt-4">
                    <DataTable ref="employeeRemunerationTable" :value="employeeRemunerationByPeriod" dataKey="id" 
                       size="small"  showGridlines scrollable v-if="isEmployeePaymentDataExist"
                    >
                        <Column
                          v-for="col in columns"
                          :key="col.field"
                          :field="col.field"
                        >
                            <template #header>
                                <div class="flex flex-col items-center">
                                    <span class="text-sm">{{ col.weekday }}</span>
                                    <span class="text-xs font-bold">{{ col.header }}</span>
                                </div>
                            </template>
                            <template #body="{ data, field }">
                                <div class="flex flex-col items-center"> 
                                    {{ data[field] * selectedEmployee.tariff}}
                                </div>
                            </template>
                        </Column>
                        <Column field="total_days" header="Наименование" class="text-center text-sm" style="min-width: 6rem" alignFrozen="right" frozen>
                            <template #body={data}>
                                <div class="flex flex-col items-center">
                                    {{ data.total_days }}
                                </div>
                            </template>
                        </Column>
                        <Column field="total_sum"  header="Итого" class="text-center text-sm" style="min-width: 6rem" alignFrozen="right" frozen>
                            <template #body={data}>
                                <div class="flex flex-col items-center">
                                    {{ data.total_sum.toLocaleString() + " " + data.currency }}
                                </div>
                            </template>
                        </Column>
                    </DataTable>
              </div>
          </Dialog>
      
          <Dialog v-model:visible="deleteProductDialog" :style="{ width: '450px' }" header="Подтверждение" :modal="true">
              <div class="flex items-center gap-4">
                  <i class="pi pi-exclamation-triangle !text-3xl" />
                  <span v-if="employee">Уверены что хотите удалить <b>{{ employee.name }}</b>?</span>
              </div>
              <template #footer>
                  <Button label="Да" icon="pi pi-check" @click="deleteProduct(employee.id)" severity="danger" />
                  <Button label="Нет" icon="pi pi-times" text @click="deleteProductDialog = false" severity="secondary" variant="text" />
              </template>
          </Dialog>

          <Dialog v-model:visible="passwordDialog" :style="{ width: '420px' }" header="Смена пароля" :modal="true">
              <div class="flex flex-col gap-2">
                  <p class="mb-1 text-sm text-slate-500" v-if="passwordEmployee">
                      Сотрудник: <span class="font-medium text-slate-900">{{ passwordEmployee.name }}</span>
                  </p>
                  <div class="py-1">
                      <FloatLabel variant="on">
                          <label for="new_password" class="block"> Новый пароль </label>
                          <InputText id="new_password" v-model="passwordForm.password" type="password" :invalid="passwordSubmitted && !passwordForm.password" fluid />
                      </FloatLabel>
                      <small v-if="passwordSubmitted && !passwordForm.password" class="text-red-500">Заполните!</small>
                  </div>
                  <div class="py-1">
                      <FloatLabel variant="on">
                          <label for="new_password_confirmation" class="block"> Новый пароль (подтверждение) </label>
                          <InputText id="new_password_confirmation" v-model="passwordForm.password_confirmation" type="password" :invalid="passwordSubmitted && !passwordForm.password_confirmation" fluid />
                      </FloatLabel>
                      <small v-if="passwordSubmitted && !passwordForm.password_confirmation" class="text-red-500">Заполните!</small>
                  </div>
              </div>
              <template #footer>
                  <Button label="Сохранить" icon="pi pi-check" @click="savePassword" severity="success" :loading="passwordSaving" />
                  <Button label="Отмена" icon="pi pi-times" text @click="passwordDialog = false" severity="secondary" variant="text" />
              </template>
          </Dialog>
        </div>
    </div>
  </div>
</template>

<script setup>
import axios from '../lib/axios'
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'
import Toast from 'primevue/toast'
import WorkingHours from './WorkingHours.vue'
// import { pass } from 'three/src/nodes/TSL.js'
import dayjs from 'dayjs'

const toast = useToast()
const loading = ref(false)
const router = useRouter()
const searchQuery = ref('')
const viewMode = ref('table')

const employee = ref({})
const employeeTable = ref()
const employeeDialog = ref(false)
const selectedEmployee = ref()
const paymentMonth = ref(dayjs().format('YYYY-MM'))
const paymentPeriods = ref([
    {
        'id': '1',
        'name': '1 пол месяца'
    },
    {
        'id': '2',
        'name': '2 пол месяца'
    }
])
const employees = ref()
const currencies = ref([])
const paymentMethods = ref([])
const paymentDialog = ref(false)
const deleteProductDialog = ref(false)
const submitted = ref(false)
const passwordDialog = ref(false)
const passwordEmployee = ref(null)
const passwordForm = ref({ password: '', password_confirmation: '' })
const passwordSubmitted = ref(false)
const passwordSaving = ref(false)
const employeeRemunerationByPeriod = ref([])
const columns = ref([])
const totalSum = ref()
const accounts = ref([])

const filteredEmployees = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return employees.value || []

    return (employees.value || []).filter((item) => {
        const haystack = [
            item?.name,
            item?.position,
            item?.barcode,
            item?.payment_method,
            item?.date
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

        return haystack.includes(q)
    })
})

const clearFilter = () => {
    searchQuery.value = ''
}

// open dialog
const openNew = () => {
    employee.value = {};
    submitted.value = false;
    employeeDialog.value = true;
}

const initialFromName = (name) => {
    const source = String(name || '').trim()
    return source ? source.charAt(0).toUpperCase() : '?'
}

const formatDate = (value) => {
    if (!value) return '—'
    return dayjs(value).format('DD.MM.YYYY')
}

const formatCurrency = (value) => {
    if(value) return value.toLocaleString('en-US', {style: 'currency', currency: 'kzt'});
    return;
};

const saveProduct = async () => {
    submitted.value = true;

    if (employee?.value.name?.trim()) {
        if (employee.value.id) {
            employee.value.inventoryStatus = employee.value.inventoryStatus.value ? employee.value.inventoryStatus.value : employee.value.inventoryStatus;
            employee.value[findIndexById(employee.value.id)] = employee.value;

            toast.add({severity:'success', summary: 'Successful', detail: 'Данные изменены!', life: 3000});
            employeeDialog.value = false;
        }   else {
            employee.value.payment_method_id = Number(employee.value.payment_method_id);

            if (employee.value.password_confirmation == employee.value.password) {
                const res = await axios.post('/api/employee', employee.value).then(res => {
                    fetchEmployee();
                    toast.add({severity:'success', summary: 'Успешно!', detail: 'Сотрудник добавлен!', life: 3000});
                    employeeDialog.value = false;
                    employee.value = {};
                }).catch(err => {
                    toast.add({severity:'error', summary: 'Ошибка!', detail: err?.response?.data?.message || 'Не удалось добавить сотрудника', life: 3000})
                })
            } else {
                toast.add({severity:'error', summary: 'Ошибка!', detail: 'Пароли не совпадают!', life: 3000})
            }
        }
    }
}

const confirmDeleteProduct = (prod) => {
    employee.value = prod
    deleteProductDialog.value = true
}

const showPayment = (employee) => {
    selectedEmployee.value = employee
    paymentDialog.value = true
}

const showChangePassword = (data) => {
    passwordEmployee.value = data
    passwordForm.value = { password: '', password_confirmation: '' }
    passwordSubmitted.value = false
    passwordDialog.value = true
}

const savePassword = async () => {
    passwordSubmitted.value = true

    if (!passwordForm.value.password || !passwordForm.value.password_confirmation) {
        return
    }

    if (passwordForm.value.password !== passwordForm.value.password_confirmation) {
        toast.add({ severity: 'error', summary: 'Ошибка!', detail: 'Пароли не совпадают!', life: 3000 })
        return
    }

    if (passwordForm.value.password.length < 8) {
        toast.add({ severity: 'error', summary: 'Ошибка!', detail: 'Пароль должен содержать минимум 8 символов', life: 3000 })
        return
    }

    passwordSaving.value = true
    try {
        await axios.put(`/api/employee/${passwordEmployee.value.id}/password`, passwordForm.value)
        toast.add({ severity: 'success', summary: 'Успешно!', detail: 'Пароль изменён', life: 3000 })
        passwordDialog.value = false
    } catch (error) {
        toast.add({ severity: 'error', summary: 'Ошибка!', detail: error?.response?.data?.message || 'Не удалось изменить пароль', life: 3000 })
    } finally {
        passwordSaving.value = false
    }
}

const isEmployeePaymentDataExist = computed(() => {
    if (selectedEmployee.value.paymentPeriod && paymentMonth.value) {
        getEmployeePayment()
        return true
    }
    return false
})

const getEmployeePayment = async () => {
    selectedEmployee.value.paymentMonth = dayjs(paymentMonth.value).format('YYYY-MM-DD') 

    const res = await axios.get(`/api/get-work-schedule-fact-by-employee?employee=${selectedEmployee.value.id}&date=${selectedEmployee.value.paymentMonth}&period=${selectedEmployee.value.paymentPeriod}`)
        .then(response => {
            let fact = response.data.work_schedule_fact;
            totalSum.value = response.data.total_sum;
            if (fact.length > 0) {
                const firstDays = fact[0].days;
                columns.value = Object.entries(firstDays).map(([date, info]) => ({
                  field: date,
                  weekday: info.weekday,
                  header: info.header,
                  date: date
                }));
            }
        
            employeeRemunerationByPeriod.value = fact.map((row) => {
                selectedEmployee.value.payment = row.total_sum
                return {
                    id: row.id,
                    employee_id: row.employee.id,
                    name: row.employee.name,
                    position: row.employee.position,
                    tariff: row.employee.tariff,
                    payment_method: row.employee.payment_method,
                    total_days: row.total_days,
                    total_sum: row.total_sum,
                    currency: row.employee.currency,
                    ...Object.fromEntries(
                        Object.entries(row.days).map(([date, info]) => [date, info.value])
                    )
                };
            });
        })
        .catch(error => {
            toast.add({severity:'error', summary: 'Ошибка!', detail: error?.response?.data?.message || 'Не удалось загрузить данные по сотруднику', life: 3000})
        });
};

const payEmployee = async () => {
    if (!selectedEmployee.value.prepayment) {
        selectedEmployee.value.prepayment = 0
    }  

    if (!selectedEmployee.value.account) {
        toast.add({severity:'error', summary: 'Ошибка!', detail: 'Не указан расчетный счет!', life: 3000})
    }   else if (selectedEmployee.value.account.amount < selectedEmployee.value.prepayment + selectedEmployee.value.payment) {
        toast.add({severity:'error', summary: 'Ошибка!', detail: 'Недостаточно средств!', life: 3000})
    }   else {
        const res = await axios.post('/api/employee/pay', selectedEmployee.value).then(res => {
            fetchEmployee();
            toast.add({severity:'success', summary: 'Успешно!', detail: 'Сотрудник оплачен!', life: 3000});
            paymentDialog.value = false;
        }).catch(error => {
            toast.add({severity:'error', summary: 'Ошибка!', detail: error?.response?.data?.message || 'Не удалось провести оплату', life: 3000})
        })
    }
}   

const deleteProduct = async (id) => {
    // employees.value = employees.value.filter(val => val.id !== employees.value.id);
    const res = await axios.delete(`/api/employee/${id}`).then(res => {
        fetchEmployee();
        toast.add({severity:'success', summary: 'Успешно!', detail: 'Сотрудник удален!', life: 3000});
        deleteProductDialog.value = false;
        employee.value = {};
    }).catch(err => {
        toast.add({severity:'error', summary: 'Ошибка!', detail: err?.response?.data?.message || 'Не удалось удалить сотрудника', life: 3000})
    })
};

const exportCSV = () => {
    employeeTable.value.exportCSV()
}

// router
const goToGraphPlan = () => {
    router.push({ name: 'WorkSchedulePlan'})
}
const goToWorkingHours = () => {
    router.push({ name: 'WorkingHours'})
}
const openGraphFact = () => {
    router.push({ name: 'WorkScheduleFact'})
}
const openSalaryReport = () => {
    router.push({ name: 'PayrollReport'})
}

// get data
const getAccounts = async () => {
  const res = await axios.get('/api/account')
  accounts.value = res.data
}
const getCurrency = async () => {
    const res = await axios.get('/api/currencies') 
    currencies.value = res.data;
}
const getPaymentMethods = async () => {
    const res = await axios.get('/api/get-payment-methods') 
    paymentMethods.value = res.data;
}

const fetchEmployee = (event) => {
    loading.value = true

    axios.get('/api/employee', {})
        .then(response => {
            employees.value = response.data.data;
            // totalRecords.value = response.data.totalRecords
            loading.value = false
        })
        .catch(error => {
            toast.add({severity:'error', summary: 'Ошибка!', detail: error?.response?.data?.message || 'Не удалось загрузить данные', life: 3000})
        });
};

onMounted(() => {
    fetchEmployee()
    getCurrency()
    getPaymentMethods()
    getAccounts()
});
</script>

<style scoped>
:deep(.employee-table),
:deep(.employee-table .p-datatable-table-container) {
  height: 100%;
}

:deep(.employee-table .p-datatable-header) {
  border: 0;
  border-bottom: 1px solid rgb(226 232 240);
  background: rgb(255 255 255);
  padding: 0 0 1rem 0;
}

:deep(.employee-table .p-datatable-thead > tr > th) {
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.875rem 1rem;
}

:deep(.employee-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(51 65 85);
  padding: 0.875rem 1rem;
  transition: background-color 150ms ease;
}

:deep(.employee-table .p-datatable-tbody > tr:hover > td) {
  background: rgba(236, 253, 245, 0.65);
}

:deep(.employee-table .p-datatable-tbody > tr > td.p-frozen-column),
:deep(.employee-table .p-datatable-thead > tr > th.p-frozen-column) {
  background: inherit;
}

:deep(.employee-table .p-datatable-tbody > tr:last-child > td) {
  border-bottom: 0;
}

:deep(.employee-table .p-datatable-footer),
:deep(.employee-table .p-paginator) {
  border-color: rgb(226 232 240);
  background: rgb(255 255 255);
}

:deep(.employee-table .p-paginator) {
  border-top: 1px solid rgb(226 232 240);
  padding: 0.75rem 1rem;
}

:deep(.employee-table .p-paginator-page),
:deep(.employee-table .p-paginator-next),
:deep(.employee-table .p-paginator-prev),
:deep(.employee-table .p-paginator-first),
:deep(.employee-table .p-paginator-last) {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.625rem;
}

:deep(.employee-table .p-paginator-page.p-paginator-page-selected) {
  background: rgb(16 185 129);
  color: rgb(255 255 255);
}

:deep(.employee-table .p-column-header-content) {
  gap: 0.375rem;
}

:deep(.employee-table .p-sortable-column:not(.p-datatable-column-sorted):hover) {
  background: rgb(241 245 249);
}

:deep(.employee-table .p-datatable-loading-overlay) {
  background: rgba(255, 255, 255, 0.72);
}
</style>
